import React, { createContext, useContext, useState, useCallback, ReactNode, Dispatch, SetStateAction } from "react"

interface WizardContextType<T> {
  wizardData: T
  setWizardData: Dispatch<SetStateAction<T>>
  updateWizardData: (changes: Partial<T>) => void
  resetWizardData: () => void
  step: number
  setStep: Dispatch<SetStateAction<number>>
  nextStep: () => void
  prevStep: () => void
}

// Provide a default empty function for reset to be overwritten by the initializer
const WizardContext = createContext<WizardContextType<any> | undefined>(undefined)

// Make WizardProvider generic; default type is WizardData for backward compatibility
export const WizardProvider = <
  T extends object
>({
  initialData,
  maxStep,
  resetData,
  children,
}: {
  initialData: T
  maxStep: number
  resetData?: () => T // optional reset function, defaults to returning initialData
  children: ReactNode
}) => {
  const [wizardData, setWizardDataState] = useState<T>(initialData)
  const [step, setStep] = useState<number>(0)

  // Patch & persist
  const updateWizardData = useCallback((changes: Partial<T>) => {
    setWizardDataState((current) => ({ ...current, ...changes }))
  }, [])

  const resetWizardData = useCallback(() => {
    if (resetData) {
      setWizardDataState(resetData())
    } else {
      setWizardDataState(initialData)
    }
    setStep(0)
  }, [resetData, initialData])

  const nextStep = useCallback(() => setStep((s) => (s < maxStep ? s + 1 : s)), [maxStep])
  const prevStep = useCallback(() => setStep((s) => (s > 0 ? s - 1 : 0)), [])

  return (
    <WizardContext.Provider
      value={{
        wizardData,
        setWizardData: setWizardDataState,
        updateWizardData,
        resetWizardData,
        step,
        setStep,
        nextStep,
        prevStep,
      }} >
      {children}
    </WizardContext.Provider>
  )
}

export function useWizard<T>() {
  const ctx = useContext(WizardContext)
  if (!ctx) throw new Error("useWizard must be used within a WizardProvider")
  return ctx as WizardContextType<T>
}